import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTablePagination,
  StyledTableSortLabel,
  Table,
  TableCell,
  TableRow,
} from '../../../components/StyledTable';
import dayjs from 'dayjs';
import { useReportsData } from '@/hooks/api/useAdvertisersData';
import type { ReportsDataItem } from '@/types/advertisers.types';

interface PerformanceData {
  date: string;
  advertiser: string;
  spend: number;
  revenue: number;
  searches: number;
  monetizedSearches: number;
  clicks: number;
  linkClicks: number;
}

interface DailyAdvertiserPerformanceProps {
  startDate?: Date;
  endDate?: Date;
  selectedAdvertiser?: string;
}

type OrderDirection = 'asc' | 'desc';
type OrderBy = keyof PerformanceData | 'pl' | 'roas' | 'ctr' | 'rpm' | 'rpc' | 'cpc' | 'coverage';

const DailyAdvertiserPerformance = ({ startDate, endDate, selectedAdvertiser }: DailyAdvertiserPerformanceProps) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<OrderBy>('date');
  const [order, setOrder] = useState<OrderDirection>('desc');
  const [showAll, setShowAll] = useState(false);

  // Use date range from props, fallback to 1st of current month to today
  const today = dayjs();
  const fromDate = startDate ? dayjs(startDate).format('YYYY-MM-DD') : today.startOf('month').format('YYYY-MM-DD');
  const toDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : today.format('YYYY-MM-DD');

  // Fetch data using React Query
  const { data: reportsResult, isLoading: loading } = useReportsData({
    fromDate,
    toDate,
  });

  // Process and aggregate data by date and advertiser
  const data = useMemo(() => {
    const rawData = reportsResult?.data || [];
    const dataMap: { [key: string]: PerformanceData } = {};
    const uniqueDatesSet = new Set<string>();

    rawData.forEach((item: ReportsDataItem) => {
      // Use the date string directly without parsing to avoid timezone issues
      const date = item.date || null;
      const advertiser = item.advertiserName || 'Unknown';

      if (!date) return;

      // Filter by selected advertiser if one is chosen
      if (selectedAdvertiser && advertiser !== selectedAdvertiser) return;

      // Track unique dates
      uniqueDatesSet.add(date);

      const key = `${date}_${advertiser}`;

      if (!dataMap[key]) {
        dataMap[key] = {
          date,
          advertiser,
          spend: 0,
          revenue: 0,
          searches: 0,
          monetizedSearches: 0,
          clicks: 0,
          linkClicks: 0,
        };
      }

      dataMap[key].spend += parseFloat(String(item.fbSpend || 0)) || 0;
      dataMap[key].revenue += parseFloat(String(item.reportedNetRevenue || 0)) || 0;
      dataMap[key].searches += parseInt(String(item.actualSearches || 0)) || 0;
      dataMap[key].monetizedSearches += parseInt(String(item.monetizedSearches || 0)) || 0;
      dataMap[key].clicks += parseInt(String(item.reportedClicks || 0)) || 0;
      dataMap[key].linkClicks += parseInt(String(item.linkClicks || 0)) || 0;
    });

    return Object.values(dataMap);
  }, [reportsResult, selectedAdvertiser]);

  // Calculate unique days from the data
  const uniqueDays = useMemo(() => {
    const datesSet = new Set<string>();
    data.forEach(item => datesSet.add(item.date));
    return datesSet.size;
  }, [data]);

  const calculateMetrics = (row: PerformanceData) => {
    const pl = row.revenue - row.spend;
    const roas = row.spend > 0 ? (row.revenue / row.spend) * 100 : 0;
    const ctr = row.searches > 0 ? (row.clicks / row.searches) * 100 : 0;
    const rpm = row.searches > 0 ? (row.revenue / row.searches) * 1000 : 0;
    const rpc = row.clicks > 0 ? row.revenue / row.clicks : 0;
    const cpc = row.linkClicks > 0 ? row.spend / row.linkClicks : 0;
    const coverage = row.searches > 0 ? (row.monetizedSearches / row.searches) * 100 : 0;

    return { pl, roas, ctr, rpm, rpc, cpc, coverage };
  };

  const sortedData = useMemo(() => {
    const sorted = [...data].sort((a, b) => {
      let aValue: any = a[orderBy as keyof PerformanceData];
      let bValue: any = b[orderBy as keyof PerformanceData];

      // Calculate computed metrics for sorting
      if (!['date', 'advertiser', 'spend', 'revenue', 'searches', 'monetizedSearches', 'clicks', 'linkClicks'].includes(orderBy)) {
        const metricsA = calculateMetrics(a);
        const metricsB = calculateMetrics(b);
        aValue = metricsA[orderBy as keyof typeof metricsA];
        bValue = metricsB[orderBy as keyof typeof metricsB];
      }

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [data, order, orderBy]);

  // Show limited data initially (10 rows per page with pagination)
  // Or show all data when "Show All" is clicked
  const displayData = showAll ? sortedData : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowAll = () => {
    setShowAll(true);
    setPage(0);
  };

  const exportToCSV = () => {
    const headers = [
      'Date',
      'Advertiser',
      'Spend',
      'Revenue',
      'P&L',
      'ROAS',
      'Searches',
      'Monetized',
      'Clicks',
      'CTR',
      'RPM',
      'RPC',
      'CPC',
      'Coverage',
      'Link Clicks',
    ];

    const rows = sortedData.map((row) => {
      const metrics = calculateMetrics(row);
      return [
        row.date,
        row.advertiser,
        row.spend.toFixed(2),
        row.revenue.toFixed(2),
        metrics.pl.toFixed(2),
        metrics.roas.toFixed(1),
        row.searches,
        row.monetizedSearches,
        row.clicks,
        metrics.ctr.toFixed(2),
        metrics.rpm.toFixed(2),
        metrics.rpc.toFixed(2),
        metrics.cpc.toFixed(2),
        metrics.coverage.toFixed(1),
        row.linkClicks,
      ];
    });

    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `daily-advertiser-performance-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: { id: OrderBy; label: string; align?: 'left' | 'right' }[] = [
    { id: 'date', label: 'Date', align: 'left' },
    { id: 'advertiser', label: 'Advertiser', align: 'left' },
    { id: 'spend', label: 'Spend', align: 'right' },
    { id: 'revenue', label: 'Revenue', align: 'right' },
    { id: 'pl', label: 'P&L', align: 'right' },
    { id: 'roas', label: 'ROAS', align: 'right' },
    { id: 'searches', label: 'Searches', align: 'right' },
    { id: 'monetizedSearches', label: 'Monetized', align: 'right' },
    { id: 'clicks', label: 'Clicks', align: 'right' },
    { id: 'ctr', label: 'CTR', align: 'right' },
    { id: 'rpm', label: 'RPM', align: 'right' },
    { id: 'rpc', label: 'RPC', align: 'right' },
    { id: 'cpc', label: 'CPC', align: 'right' },
    { id: 'coverage', label: 'Coverage', align: 'right' },
    { id: 'linkClicks', label: 'Link Clicks', align: 'right' },
  ];

  return (
    <Card
      sx={{
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
        border: 'none',
      }}
    >
      <CardContent sx={{ padding: '25px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Daily Advertiser Performance Summary
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {sortedData.length} records
            </Typography>
            {!showAll && sortedData.length > rowsPerPage && (
              <Button
                onClick={handleShowAll}
                variant="outlined"
                size="small"
              >
                Show All {uniqueDays} Days
              </Button>
            )}
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              variant="contained"
              size="small"
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        <StyledTableContainer sx={{ maxHeight: 800 }}>
          <Table stickyHeader>
            <StyledTableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align || 'left'}>
                    <StyledTableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </StyledTableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </StyledTableHead>

            <StyledTableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">Loading data...</Typography>
                  </TableCell>
                </TableRow>
              ) : displayData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">No data available</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayData.map((row, index) => {
                  const metrics = calculateMetrics(row);
                  // Parse YYYY-MM-DD format directly without timezone conversion
                  const [year, month, day] = row.date.split('-');
                  const formattedDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

                  return (
                    <TableRow key={`${row.date}_${row.advertiser}_${index}`}>
                      <TableCell>{formattedDate}</TableCell>
                      <TableCell>{row.advertiser}</TableCell>
                      <TableCell align="right" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
                        ${row.spend.toFixed(2)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
                        ${row.revenue.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: metrics.pl >= 0 ? theme.palette.success.main : theme.palette.error.main,
                          fontWeight: 500,
                        }}
                      >
                        ${metrics.pl.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: metrics.roas >= 100 ? theme.palette.success.main : theme.palette.error.main,
                          fontWeight: 500,
                        }}
                      >
                        {metrics.roas.toFixed(1)}%
                      </TableCell>
                      <TableCell align="right">{row.searches.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.monetizedSearches.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{metrics.ctr.toFixed(2)}%</TableCell>
                      <TableCell align="right">${metrics.rpm.toFixed(2)}</TableCell>
                      <TableCell align="right">${metrics.rpc.toFixed(2)}</TableCell>
                      <TableCell align="right">${metrics.cpc.toFixed(2)}</TableCell>
                      <TableCell align="right">{metrics.coverage.toFixed(1)}%</TableCell>
                      <TableCell align="right">{row.linkClicks.toLocaleString()}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>

        {!showAll && (
          <StyledTablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DailyAdvertiserPerformance;
